/**
 * VoicePicker — settings panel for choosing an ElevenLabs voice.
 * Preview plays a sample phrase in the chosen voice.
 * Select persists the choice via PATCH /user/voice.
 *
 * Gated to Pro / Entrepreneur in SettingsPage.
 */

import { useState, useRef, useEffect } from 'react';
import { Loader2, Play, Square, Check, Mic2 } from 'lucide-react';
import { speakStream, setVoicePreference } from '../lib/api';
import { playStreamingMp3 } from '../lib/voicePlayer';
import { VOICES, DEFAULT_VOICE_ID, SAMPLE_PHRASE } from '../lib/voices';

export default function VoicePicker({ currentVoiceId, onChange }) {
  const [selected, setSelected] = useState(currentVoiceId || DEFAULT_VOICE_ID);
  const [saving, setSaving] = useState(null);   // voice_id being saved
  const [previewing, setPreviewing] = useState(null); // voice_id previewing
  const [error, setError] = useState(null);
  const playerRef = useRef(null);

  useEffect(() => {
    if (currentVoiceId) setSelected(currentVoiceId);
  }, [currentVoiceId]);

  useEffect(() => {
    return () => {
      if (playerRef.current) { try { playerRef.current.stop(); } catch { /* noop */ } }
    };
  }, []);

  const stopPreview = () => {
    if (playerRef.current) {
      try { playerRef.current.stop(); } catch { /* noop */ }
      playerRef.current = null;
    }
    setPreviewing(null);
  };

  const handlePreview = async (voiceId) => {
    if (previewing === voiceId) { stopPreview(); return; }
    stopPreview();
    setError(null);
    setPreviewing(voiceId);
    try {
      const response = await speakStream(SAMPLE_PHRASE, voiceId);
      const player = await playStreamingMp3(response, {
        onEnded: () => { setPreviewing(null); playerRef.current = null; },
        onError: (e) => { setError(e.message); setPreviewing(null); },
      });
      playerRef.current = player;
    } catch (e) {
      setError(e.message);
      setPreviewing(null);
    }
  };

  const handleSelect = async (voiceId) => {
    if (voiceId === selected) return;
    setSaving(voiceId);
    setError(null);
    try {
      await setVoicePreference(voiceId);
      setSelected(voiceId);
      if (onChange) onChange(voiceId);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-1">
        <Mic2 size={16} className="text-blue-400" />
        <h3 className="text-sm font-medium text-slate-200">Coaching voice</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Choose the voice The Navigator uses when speaking. Preview any voice before selecting.
      </p>

      <div className="space-y-2">
        {VOICES.map((voice) => {
          const isSelected = voice.id === selected;
          const isPreviewing = previewing === voice.id;
          const isSaving = saving === voice.id;
          return (
            <div
              key={voice.id}
              className={`flex items-center justify-between gap-3 p-3 rounded-md border transition-colors ${
                isSelected
                  ? 'bg-blue-900/20 border-blue-700/50'
                  : 'bg-slate-900/40 border-slate-700/40 hover:border-slate-600/60'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-200 truncate">{voice.name}</span>
                  {isSelected && (
                    <span className="flex items-center gap-1 text-[11px] text-blue-300 bg-blue-900/40 px-1.5 py-0.5 rounded-full">
                      <Check size={10} /> Selected
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 truncate">{voice.description}</p>
              </div>

              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => handlePreview(voice.id)}
                  className="flex items-center gap-1 text-xs text-slate-300 hover:text-white bg-slate-700/60 hover:bg-slate-700 px-2.5 py-1.5 rounded-md transition-colors"
                  title={isPreviewing ? 'Stop preview' : 'Preview this voice'}
                >
                  {isPreviewing
                    ? <><Square size={11} /> Stop</>
                    : <><Play size={11} /> Preview</>}
                </button>
                <button
                  onClick={() => handleSelect(voice.id)}
                  disabled={isSelected || isSaving}
                  className={`text-xs px-2.5 py-1.5 rounded-md transition-colors ${
                    isSelected
                      ? 'text-slate-500 cursor-default'
                      : 'text-blue-300 hover:text-white bg-blue-900/30 hover:bg-blue-800/50'
                  }`}
                >
                  {isSaving
                    ? <Loader2 size={11} className="animate-spin" />
                    : isSelected ? 'Current' : 'Select'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <p className="text-xs text-red-400 mt-3">{error}</p>
      )}
    </div>
  );
}
